import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BASE_URL, URL } from "../../redux/constants";
import { setCartItem } from "../../redux/features/cartSlice";
import {
  useAiRematchBomMutation,
  useBuildBomCartItemsMutation,
  useCreateBlankBomMutation,
  useCreateClipboardBomMutation,
  useCreateManualBomMutation,
  useCreateBomItemMutation,
  useDeleteBomItemsMutation,
  useGetBomOrderQuery,
  useGetBomRelatedOrdersQuery,
  usePreviewBomPurchaseMutation,
  useReparseBomMutation,
  useRematchBomMutation,
  useReviewBomAiSuggestionMutation,
  useSearchBomItemCandidatesMutation,
  useUpdateBomItemMutation,
  useUpdateBomItemsMutation,
  useUploadBomMutation,
} from "../../redux/api/bomApi";
import UniverBomSheet, {
  BOM_SHEET_COLUMNS,
  MATCH_STATUS,
  STATUS_LABELS,
  buildBomRawColumns,
  extractBomSheetRows,
  formatMoney,
  getSelectedBomItemIds,
} from "./UniverBomSheet";
import BomHistoryPanel from "./BomHistoryPanel";
import "./BomOrderPage.css";

/**
 * 获取 BOM 本地会话 ID，游客上传时用于访问自己的 BOM 任务。
 * @returns {string} BOM 会话 ID
 */
const getBomSessionId = () => {
  const storageKey = "bomOrderSessionId";
  const existingId = window.localStorage.getItem(storageKey);
  if (existingId) return existingId;

  const nextId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(storageKey, nextId);
  return nextId;
};

/**
 * 判断 BOM 明细是否允许加入购物车。
 * @param {Object} item BOM 明细
 * @returns {boolean} 是否允许选择
 */
const canSelectBomItem = (item = {}) => {
  return item.matchStatus === "matched" && item.selectedProduct && !item.needQuote;
};

/**
 * 计算 BOM 完全匹配率。
 * @param {Object} summary BOM 汇总信息
 * @returns {number} 匹配率
 */
const getMatchRate = (summary = {}) => {
  const totalRows = Number(summary.totalRows || 0);
  if (!totalRows) return 0;
  return Math.round((Number(summary.matchedRows || 0) / totalRows) * 1000) / 10;
};

/**
 * 获取标准 BOM 模板下载地址。
 * @returns {string} 模板接口地址
 */
const getBomTemplateUrl = () => `${BASE_URL || ""}${URL}/bom/template`;

const BOM_COLUMN_PREFERENCES_KEY = "bomOrderColumnPreferences";
const MAX_AI_REMATCH_ROWS = 20;
const BOM_MAPPING_FIELDS = [
  { key: "originalMpn", label: "Part Number (MPN)", requiredGroup: "part" },
  { key: "originalSupplierPartNo", label: "Supplier Part Number / C-number", requiredGroup: "part" },
  { key: "unitQty", label: "Quantity", required: true },
  { key: "originalManufacturer", label: "Manufacturer / Brand" },
  { key: "packageSize", label: "Package / Size" },
  { key: "originalDescription", label: "Description / Parameters" },
  { key: "designator", label: "Designator" },
  { key: "supplier", label: "Supplier" },
  { key: "productionSurplusQty", label: "Spare Quantity" },
];

/**
 * 读取并修复 BOM 云表列偏好，兼容后续新增列。
 * @returns {{order: string[], hiddenKeys: string[]}} 列顺序和隐藏列
 */
const loadBomColumnPreferences = () => {
  const defaultOrder = BOM_SHEET_COLUMNS.map((column) => column.key);
  if (typeof window === "undefined") return { order: defaultOrder, hiddenKeys: [] };

  try {
    const saved = JSON.parse(window.localStorage.getItem(BOM_COLUMN_PREFERENCES_KEY) || "{}");
    const savedOrder = Array.isArray(saved.order) ? saved.order : [];
    const order = Array.from(new Set(savedOrder.filter((key) => typeof key === "string")));
    if (order.length === 0) order.push(...defaultOrder);
    const hiddenKeys = Array.isArray(saved.hiddenKeys)
      ? Array.from(new Set(saved.hiddenKeys.filter((key) => typeof key === "string")))
      : [];

    return {
      order,
      hiddenKeys: hiddenKeys.length === defaultOrder.length ? [] : hiddenKeys,
    };
  } catch (error) {
    return { order: defaultOrder, hiddenKeys: [] };
  }
};

/**
 * 将本地列偏好与当前 BOM 的标准列、原始列合并。
 * @param {{order: string[], hiddenKeys: string[]}} preferences 本地列偏好
 * @param {Object[]} columns 当前全部列
 * @returns {{order: string[], hiddenKeys: string[]}} 当前有效列偏好
 */
const reconcileBomColumnPreferences = (preferences, columns) => {
  const validKeys = new Set(columns.map((column) => column.key));
  const savedOrder = (preferences.order || []).filter((key) => validKeys.has(key));
  const order = Array.from(new Set(savedOrder));
  columns.forEach((column) => {
    if (!order.includes(column.key)) order.push(column.key);
  });

  const hiddenKeys = new Set(
    (preferences.hiddenKeys || []).filter((key) => validKeys.has(key)),
  );
  columns.forEach((column) => {
    if (!savedOrder.includes(column.key) && column.defaultHidden) hiddenKeys.add(column.key);
  });

  return { order, hiddenKeys: Array.from(hiddenKeys) };
};

/**
 * 将 BOM 云表列偏好保存到浏览器本地。
 * @param {{order: string[], hiddenKeys: string[]}} preferences 列偏好
 */
const saveBomColumnPreferences = (preferences) => {
  window.localStorage.setItem(BOM_COLUMN_PREFERENCES_KEY, JSON.stringify(preferences));
};

const BomOrderPage = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { bomId } = useParams();
  const fileInputRef = useRef(null);
  const univerApiRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveInFlightRef = useRef(false);
  const autoSavePromiseRef = useRef(Promise.resolve(true));
  const pendingAutoSaveIdsRef = useRef(new Set());
  const pendingAutoSavePatchesRef = useRef(new Map());
  const [sessionId] = useState(getBomSessionId);
  const [bomOrder, setBomOrder] = useState(null);
  const [activeStatuses, setActiveStatuses] = useState(new Set(MATCH_STATUS));
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [purchaseSets, setPurchaseSets] = useState(1);
  const [onlySelected, setOnlySelected] = useState(false);
  const [columnPreferences, setColumnPreferences] = useState(loadBomColumnPreferences);
  const [columnDraft, setColumnDraft] = useState(null);
  const [headerMappingDraft, setHeaderMappingDraft] = useState(null);
  const [draggedColumnKey, setDraggedColumnKey] = useState("");
  const [manualInputOpen, setManualInputOpen] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [clipboardInputOpen, setClipboardInputOpen] = useState(false);
  const [clipboardInput, setClipboardInput] = useState("");
  const [candidateReviewItemId, setCandidateReviewItemId] = useState("");
  const [candidateKeyword, setCandidateKeyword] = useState("");
  const [searchedCandidates, setSearchedCandidates] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [rowOperationStates, setRowOperationStates] = useState({});
  const [purchasePreview, setPurchasePreview] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [createBlankBom, { isLoading: isCreatingBlankBom }] = useCreateBlankBomMutation();
  const [createClipboardBom, { isLoading: isCreatingClipboardBom }] = useCreateClipboardBomMutation();
  const [createManualBom, { isLoading: isCreatingManualBom }] = useCreateManualBomMutation();
  const [uploadBom, { isLoading: isUploading }] = useUploadBomMutation();
  const [createBomItem, { isLoading: isCreatingItem }] = useCreateBomItemMutation();
  const [deleteBomItems, { isLoading: isDeletingItems }] = useDeleteBomItemsMutation();
  const [reparseBom, { isLoading: isReparsing }] = useReparseBomMutation();
  const [updateBomItems] = useUpdateBomItemsMutation();
  const [updateBomItem, { isLoading: isConfirmingCandidate }] = useUpdateBomItemMutation();
  const [searchBomItemCandidates, { isLoading: isSearchingCandidates }] = useSearchBomItemCandidatesMutation();
  const [rematchBom, { isLoading: isRematching }] = useRematchBomMutation();
  const [aiRematchBom, { isLoading: isAiRematching }] = useAiRematchBomMutation();
  const [reviewBomAiSuggestion, { isLoading: isReviewingAi }] = useReviewBomAiSuggestionMutation();
  const [buildBomCartItems, { isLoading: isAdding }] = useBuildBomCartItemsMutation();
  const [previewBomPurchase, { isLoading: isLoadingPurchasePreview }] = usePreviewBomPurchaseMutation();
  const {
    data: restoredBomData,
    error: restoreBomError,
    isLoading: isRestoringBom,
  } = useGetBomOrderQuery(
    { id: bomId, sessionId },
    { skip: !bomId },
  );
  const {
    data: relatedOrderData,
    isFetching: isLoadingRelatedOrders,
  } = useGetBomRelatedOrdersQuery(
    { id: bomOrder?._id, sessionId },
    { skip: !isAuthenticated || !bomOrder?._id },
  );
  const relatedOrders = relatedOrderData?.orders || [];
  const isCreatingInitialBom = isUploading
    || isCreatingBlankBom
    || isCreatingManualBom
    || isCreatingClipboardBom
    || isRestoringBom;

  const summary = bomOrder?.summary || {};
  const matchRate = getMatchRate(summary);
  const aiPendingItems = useMemo(
    () => (bomOrder?.items || []).filter((item) => item.matchSource === "ai" && item.requiresConfirmation),
    [bomOrder?.items],
  );
  const localPendingItems = useMemo(
    () => (bomOrder?.items || []).filter((item) => (
      item.matchStatus === "pending_confirm"
      && item.matchSource !== "ai"
      && Array.isArray(item.candidates)
      && item.candidates.length > 0
    )),
    [bomOrder?.items],
  );
  const candidateReviewItem = useMemo(() => {
    const requestedItem = (bomOrder?.items || []).find((item) => item._id === candidateReviewItemId);
    return requestedItem || localPendingItems[0] || null;
  }, [bomOrder?.items, candidateReviewItemId, localPendingItems]);
  const visibleCandidates = searchedCandidates || candidateReviewItem?.candidates || [];
  const allColumns = useMemo(
    () => [...BOM_SHEET_COLUMNS, ...buildBomRawColumns(bomOrder)],
    [bomOrder],
  );
  const effectiveColumnPreferences = useMemo(
    () => reconcileBomColumnPreferences(columnPreferences, allColumns),
    [allColumns, columnPreferences],
  );
  const visibleColumns = useMemo(() => {
    const columnMap = new Map(allColumns.map((column) => [column.key, column]));
    const hiddenKeys = new Set(effectiveColumnPreferences.hiddenKeys);
    return effectiveColumnPreferences.order
      .map((key) => columnMap.get(key))
      .filter((column) => column && !hiddenKeys.has(column.key));
  }, [allColumns, effectiveColumnPreferences]);
  const headerMappingColumns = useMemo(() => {
    if (!headerMappingDraft) return [];
    return bomOrder?.sourceRows?.[headerMappingDraft.headerRowIndex] || [];
  }, [bomOrder?.sourceRows, headerMappingDraft]);

  const filteredItems = useMemo(() => {
    const items = bomOrder?.items || [];
    return items.filter((item) => {
      const statusMatched = activeStatuses.has(item.matchStatus);
      const selectedMatched = !onlySelected || selectedItemIds.has(item._id);
      return statusMatched && selectedMatched;
    });
  }, [activeStatuses, bomOrder?.items, onlySelected, selectedItemIds]);
  const selectedTotalAmount = useMemo(() => {
    return (bomOrder?.items || []).reduce((total, item) => {
      if (!selectedItemIds.has(item._id) || !canSelectBomItem(item)) return total;
      return total + Number(item.subtotal || 0);
    }, 0);
  }, [bomOrder?.items, selectedItemIds]);
  const selectedItemIdsKey = useMemo(
    () => Array.from(selectedItemIds).sort().join(","),
    [selectedItemIds],
  );
  const purchaseTotalAmount = purchasePreview?.summary?.totalAmount ?? selectedTotalAmount;
  const hasPurchaseBlockingIssue = Boolean(
    purchasePreview?.summary?.insufficientGroups || purchasePreview?.summary?.quoteGroups,
  );
  const rowOperationValues = Object.values(rowOperationStates);
  const hasPendingRowChanges = rowOperationValues.some((status) => (
    status === "saving" || status === "matching"
  ));
  const hasRowSaveErrors = rowOperationValues.includes("error");
  const hasUnsafePurchaseEdits = hasPendingRowChanges || hasRowSaveErrors;

  /**
   * 选中范围变化后刷新合并采购预览，展示 MOQ、阶梯价和库存差异。
   */
  useEffect(() => {
    let cancelled = false;
    if (!bomOrder?._id || !selectedItemIdsKey) {
      setPurchasePreview(null);
      return undefined;
    }

    previewBomPurchase({
      bomId: bomOrder._id,
      sessionId,
      itemIds: selectedItemIdsKey.split(","),
    }).unwrap()
      .then((response) => {
        if (!cancelled) setPurchasePreview(response?.preview || null);
      })
      .catch(() => {
        if (!cancelled) setPurchasePreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bomOrder?._id, bomOrder?.updatedAt, previewBomPurchase, selectedItemIdsKey, sessionId]);

  /**
   * 页面卸载时清理尚未执行的自动保存计时器。
   */
  useEffect(() => () => {
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
  }, []);

  /**
   * 用后端返回的 BOM 任务刷新当前表格和默认选中项。
   * @param {Object|null} nextBomOrder BOM 任务
   */
  const refreshBomOrder = useCallback((nextBomOrder) => {
    setBomOrder(nextBomOrder || null);
    setSelectedItemIds(
      new Set((nextBomOrder?.items || []).filter((item) => item.selected).map((item) => item._id))
    );
    if (nextBomOrder?._id && String(nextBomOrder._id) !== String(bomId || "")) {
      navigate(`/bom-order/${nextBomOrder._id}`, { replace: true });
    }
  }, [bomId, navigate]);

  /**
   * 根据 URL 中的 BOM ID 恢复当前会话可访问的 BOM。
   */
  useEffect(() => {
    if (restoredBomData?.bomOrder) refreshBomOrder(restoredBomData.bomOrder);
  }, [refreshBomOrder, restoredBomData]);

  /**
   * 显示 BOM 恢复失败原因。
   */
  useEffect(() => {
    if (restoreBomError) {
      toast.error(restoreBomError?.data?.message || "Failed to restore BOM");
    }
  }, [restoreBomError]);

  /**
   * 记录 Univer API，供保存表格编辑时读取当前工作簿。
   * @param {Object|null} univerAPI Univer facade API
   */
  const handleUniverReady = useCallback((univerAPI) => {
    univerApiRef.current = univerAPI;
  }, []);

  /**
   * 上传 BOM 文件并进入在线表格。
   * @param {File} file BOM 文件
   */
  const handleUploadFile = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("sessionId", sessionId);

    try {
      const response = await uploadBom(formData).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM online spreadsheet generated");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to upload BOM");
    }
  };

  /**
   * 处理文件输入框变化。
   * @param {Event} event 输入事件
   */
  const handleFileChange = (event) => {
    handleUploadFile(event.target.files?.[0]);
    event.target.value = "";
  };

  /**
   * 触发选择 BOM 文件。
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  /**
   * 创建带一条可编辑物料行的空白 BOM 并进入工作台。
   */
  const handleCreateBlankBom = async () => {
    try {
      const response = await createBlankBom({ sessionId }).unwrap();
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Blank BOM created");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to create blank BOM");
    }
  };

  /**
   * 提交逐行录入的料号和数量，并进入匹配后的 BOM 工作台。
   * @param {Event} event 表单提交事件
   */
  const handleCreateManualBom = async (event) => {
    event.preventDefault();
    if (!manualInput.trim()) {
      toast.info("Enter at least one part number");
      return;
    }

    try {
      const response = await createManualBom({ sessionId, input: manualInput }).unwrap();
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      setManualInput("");
      setManualInputOpen(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Manual BOM generated");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to generate manual BOM");
    }
  };

  /**
   * 提交从 Excel/WPS 粘贴的二维表格，并进入匹配后的 BOM 工作台。
   * @param {Event} event 表单提交事件
   */
  const handleCreateClipboardBom = async (event) => {
    event.preventDefault();
    if (!clipboardInput.trim()) {
      toast.info("Paste a spreadsheet range with headers first");
      return;
    }

    try {
      const response = await createClipboardBom({ sessionId, input: clipboardInput }).unwrap();
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      setClipboardInput("");
      setClipboardInputOpen(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Clipboard BOM generated");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to generate clipboard BOM");
    }
  };

  /**
   * 切换匹配状态筛选。
   * @param {string} status 匹配状态
   */
  const toggleStatus = (status) => {
    const nextStatuses = new Set(activeStatuses);
    if (nextStatuses.has(status)) {
      nextStatuses.delete(status);
    } else {
      nextStatuses.add(status);
    }
    setActiveStatuses(nextStatuses);
  };

  /**
   * 切换全部状态筛选。
   * @param {boolean} checked 是否全选
   */
  const toggleAllStatus = (checked) => {
    setActiveStatuses(new Set(checked ? MATCH_STATUS : []));
  };

  /**
   * 切换当前筛选范围内可加购明细的全选状态。
   * @param {boolean} checked 是否全选
   */
  const toggleVisibleSelection = async (checked) => {
    const selectableItems = filteredItems.filter(canSelectBomItem);
    if (selectableItems.length === 0) return;
    const nextIds = new Set(selectedItemIds);
    selectableItems.forEach((item) => {
      if (checked) nextIds.add(item._id);
      else nextIds.delete(item._id);
    });
    setSelectedItemIds(nextIds);

    try {
      const response = await updateBomItems({
        bomId: bomOrder._id,
        sessionId,
        patches: selectableItems.map((item) => ({ itemId: item._id, selected: checked })),
      }).unwrap();
      refreshBomOrder(response?.bomOrder || bomOrder);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save bulk selection");
    }
  };

  /**
   * 按采购套数批量刷新购买数量。
   */
  const applyPurchaseSets = async () => {
    if (!bomOrder?._id) return;
    const safeSets = Math.max(1, Number(purchaseSets || 1));

    try {
      const response = await updateBomItems({
        bomId: bomOrder._id,
        sessionId,
        patches: (bomOrder.items || []).map((item) => {
          const unitQty = Number(item.normalized?.unitQty || item.original?.unitQty || 1);
          const spareQty = Number(item.normalized?.productionSurplusQty || 0);
          const quantity = unitQty * safeSets + spareQty;

          return {
            itemId: item._id,
            quantity,
            normalized: {
              orderQty: quantity,
            },
          };
        }),
      }).unwrap();
      refreshBomOrder(response?.bomOrder || bomOrder);
      toast.success("Purchase sets applied");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to apply purchase sets");
    }
  };

  /**
   * 保存 Univer 表格中的可编辑 BOM 字段。
   * @param {Object} options 保存选项
   * @param {boolean} options.silent 是否静默保存
   * @param {string[]} options.itemIds 仅保存指定行
   * @param {Object[]} options.patchesOverride 已在编辑事件中捕获的补丁
   * @returns {Promise<boolean>} 是否保存成功
   */
  const saveSheetEdits = async ({ silent = false, itemIds = [], patchesOverride = null } = {}) => {
    if (!bomOrder?._id || !univerApiRef.current) return true;

    const itemIdSet = new Set(itemIds);
    const patches = (patchesOverride || extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns))
      .filter((patch) => itemIdSet.size === 0 || itemIdSet.has(String(patch.itemId)));
    if (patches.length === 0) return true;
    setRowOperationStates((states) => ({
      ...states,
      ...Object.fromEntries(patches.map((patch) => [patch.itemId, states[patch.itemId] || "saving"])),
    }));

    try {
      const response = await updateBomItems({
        bomId: bomOrder._id,
        sessionId,
        patches,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || bomOrder);
      const rematchedIds = new Set(response?.rematchedItemIds || []);
      setRowOperationStates((states) => ({
        ...states,
        ...Object.fromEntries(patches.map((patch) => [
          patch.itemId,
          pendingAutoSaveIdsRef.current.has(String(patch.itemId))
            ? states[patch.itemId] || "saving"
            : (rematchedIds.has(String(patch.itemId)) ? "rematched" : "saved"),
        ])),
      }));
      if (!silent) toast.success("Spreadsheet changes saved");
      return true;
    } catch (error) {
      setRowOperationStates((states) => ({
        ...states,
        ...Object.fromEntries(patches.map((patch) => [patch.itemId, "error"])),
      }));
      toast.error(error?.data?.message || "Failed to save spreadsheet changes");
      return false;
    }
  };

  /**
   * 串行刷新待自动保存行，避免连续编辑产生并发响应覆盖新结果。
   * @returns {Promise<boolean>} 所有当前待保存修改是否成功
   */
  const flushPendingAutoSave = async () => {
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (autoSaveInFlightRef.current) {
      const currentSaved = await autoSavePromiseRef.current;
      if (!currentSaved) return false;
      if (pendingAutoSaveIdsRef.current.size > 0) return flushPendingAutoSave();
      return true;
    }
    if (pendingAutoSaveIdsRef.current.size === 0) return true;
    const itemIds = Array.from(pendingAutoSaveIdsRef.current);
    const patches = itemIds
      .map((itemId) => pendingAutoSavePatchesRef.current.get(itemId))
      .filter(Boolean);
    pendingAutoSaveIdsRef.current.clear();
    itemIds.forEach((itemId) => pendingAutoSavePatchesRef.current.delete(itemId));
    autoSaveInFlightRef.current = true;
    const savePromise = saveSheetEdits({ silent: true, itemIds, patchesOverride: patches });
    autoSavePromiseRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      autoSaveInFlightRef.current = false;
      if (pendingAutoSaveIdsRef.current.size > 0) {
        autoSaveTimerRef.current = window.setTimeout(flushPendingAutoSave, 100);
      }
    }
  };

  /**
   * 响应 Univer 单元格变化，标记受影响行并防抖执行行级保存和重新匹配。
   * @param {Object} params Univer 值变化事件
   */
  const handleSheetValueChanged = (params = {}) => {
    const matchingFields = new Set([
      "originalDescription",
      "originalManufacturer",
      "originalMpn",
      "originalSupplierPartNo",
      "packageSize",
    ]);
    const nextStates = {};
    (params.effectedRanges || []).forEach((effectedRange) => {
      const range = effectedRange?.getRange?.();
      if (!range) return;
      const startRow = Math.max(1, Number(range.startRow || 0));
      const endRow = Math.min(filteredItems.length, Number(range.endRow ?? range.startRow ?? 0));
      const startColumn = Math.max(0, Number(range.startColumn || 0));
      const endColumn = Math.min(visibleColumns.length - 1, Number(range.endColumn ?? range.startColumn ?? 0));
      const affectedColumns = visibleColumns.slice(startColumn, endColumn + 1);
      if (!affectedColumns.some((column) => column.editable)) return;
      const needsRematch = affectedColumns
        .some((column) => matchingFields.has(column.field));

      for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
        const itemId = filteredItems[rowIndex - 1]?._id;
        if (!itemId) continue;
        pendingAutoSaveIdsRef.current.add(String(itemId));
        nextStates[itemId] = needsRematch ? "matching" : "saving";
      }
    });
    if (Object.keys(nextStates).length === 0) return;
    const changedIds = new Set(Object.keys(nextStates));
    extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns)
      .filter((patch) => changedIds.has(String(patch.itemId)))
      .forEach((patch) => pendingAutoSavePatchesRef.current.set(String(patch.itemId), patch));
    setPurchasePreview(null);
    setRowOperationStates((states) => ({ ...states, ...nextStates }));
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(flushPendingAutoSave, 800);
  };

  /**
   * 打开自定义列面板并创建一份待应用配置。
   */
  const openColumnSettings = () => {
    setHeaderMappingDraft(null);
    setColumnDraft({
      order: [...effectiveColumnPreferences.order],
      hiddenKeys: [...effectiveColumnPreferences.hiddenKeys],
    });
  };

  /**
   * 打开表头映射纠错面板，载入当前解析结果。
   */
  const openHeaderMapping = () => {
    if (!Array.isArray(bomOrder?.sourceRows) || bomOrder.sourceRows.length === 0) {
      toast.info("This BOM has no saved source rows for header mapping");
      return;
    }
    setColumnDraft(null);
    setHeaderMappingDraft({
      headerRowIndex: Number(bomOrder.headerRowIndex || 0),
      fieldMap: { ...(bomOrder.fieldMap || {}) },
    });
  };

  /**
   * 更新单个标准字段对应的原始列。
   * @param {string} fieldName 标准字段名
   * @param {string} columnIndex 原始列索引
   */
  const updateHeaderMappingField = (fieldName, columnIndex) => {
    setHeaderMappingDraft((draft) => {
      if (!draft) return draft;
      const fieldMap = { ...draft.fieldMap };
      if (columnIndex === "") delete fieldMap[fieldName];
      else fieldMap[fieldName] = Number(columnIndex);
      return { ...draft, fieldMap };
    });
  };

  /**
   * 提交表头行和字段映射，重新解析并匹配当前 BOM。
   */
  const applyHeaderMapping = async () => {
    if (!bomOrder?._id || !headerMappingDraft) return;
    try {
      const response = await reparseBom({
        bomId: bomOrder._id,
        sessionId,
        headerRowIndex: headerMappingDraft.headerRowIndex,
        fieldMap: headerMappingDraft.fieldMap,
      }).unwrap();
      setHeaderMappingDraft(null);
      setActiveStatuses(new Set(MATCH_STATUS));
      setOnlySelected(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Headers remapped and BOM rematched");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to reparse BOM headers");
    }
  };

  /**
   * 切换草稿中指定列的显示状态，至少保留一列。
   * @param {string} columnKey 列标识
   */
  const toggleDraftColumn = (columnKey) => {
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const hiddenKeys = new Set(draft.hiddenKeys);
      if (hiddenKeys.has(columnKey)) {
        hiddenKeys.delete(columnKey);
      } else if (draft.order.length - hiddenKeys.size > 1) {
        hiddenKeys.add(columnKey);
      } else {
        toast.info("At least one column must remain visible");
      }
      return { ...draft, hiddenKeys: Array.from(hiddenKeys) };
    });
  };

  /**
   * 将草稿中的列移动一个位置，供键盘和按钮操作使用。
   * @param {string} columnKey 列标识
   * @param {-1|1} direction 移动方向
   */
  const moveDraftColumn = (columnKey, direction) => {
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const order = [...draft.order];
      const currentIndex = order.indexOf(columnKey);
      const targetIndex = currentIndex + direction;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= order.length) return draft;
      [order[currentIndex], order[targetIndex]] = [order[targetIndex], order[currentIndex]];
      return { ...draft, order };
    });
  };

  /**
   * 将拖动列放到目标列之前。
   * @param {string} targetKey 目标列标识
   */
  const dropDraftColumn = (targetKey) => {
    if (!draggedColumnKey || draggedColumnKey === targetKey) return;
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const order = draft.order.filter((key) => key !== draggedColumnKey);
      const targetIndex = order.indexOf(targetKey);
      order.splice(targetIndex < 0 ? order.length : targetIndex, 0, draggedColumnKey);
      return { ...draft, order };
    });
    setDraggedColumnKey("");
  };

  /**
   * 保存当前编辑后应用并持久化自定义列配置。
   */
  const applyColumnSettings = async () => {
    if (!columnDraft) return;
    const saved = await saveSheetEdits({ silent: true });
    if (!saved) return;
    saveBomColumnPreferences(columnDraft);
    setColumnPreferences(columnDraft);
    setColumnDraft(null);
    toast.success("Column settings saved");
  };

  /**
   * 将列设置草稿恢复为项目默认值。
   */
  const resetColumnSettings = () => {
    setColumnDraft({
      order: allColumns.map((column) => column.key),
      hiddenKeys: allColumns.filter((column) => column.defaultHidden).map((column) => column.key),
    });
  };

  /**
   * 在当前 BOM 云表末尾新增一条可编辑物料行。
   */
  const handleCreateItem = async () => {
    if (!bomOrder?._id) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await createBomItem({
        bomId: bomOrder._id,
        sessionId,
        normalized: { unitQty: 1, productionSurplusQty: 0 },
      }).unwrap();
      setActiveStatuses((statuses) => new Set([...statuses, "invalid"]));
      setOnlySelected(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Item added. Enter a part number or C-number");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add item");
    }
  };

  /**
   * 删除 Univer 当前选区覆盖的 BOM 物料行。
   */
  const handleDeleteSelectedRows = async () => {
    if (!bomOrder?._id || !univerApiRef.current) return;

    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems);
    if (itemIds.length === 0) {
      toast.info("Select the item rows to delete in the spreadsheet first");
      return;
    }
    if (!window.confirm(`Delete the ${itemIds.length} selected item row(s)?`)) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await deleteBomItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success(`${response?.deletedCount || itemIds.length} item row(s) deleted`);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete items");
    }
  };

  /**
   * 重新匹配当前 BOM 任务。
   */
  const handleRematch = async () => {
    if (!bomOrder?._id) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await rematchBom({ bomId: bomOrder._id, sessionId }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM rematched");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to rematch BOM");
    }
  };

  /**
   * 使用 AI 为当前选区中的无法匹配 BOM 行生成待确认候选。
   */
  const handleAiRematch = async () => {
    if (!bomOrder?._id || !univerApiRef.current || Number(summary.unmatchedRows || 0) === 0) return;

    const unmatchedItemIds = new Set(
      (bomOrder.items || [])
        .filter((item) => item.matchStatus === "unmatched")
        .map((item) => String(item._id)),
    );
    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems)
      .filter((itemId) => unmatchedItemIds.has(String(itemId)));

    if (itemIds.length === 0) {
      toast.info("Select the unmatched item rows to AI match in the spreadsheet first");
      return;
    }
    if (itemIds.length > MAX_AI_REMATCH_ROWS) {
      toast.info(`Select no more than ${MAX_AI_REMATCH_ROWS} unmatched rows at a time`);
      return;
    }

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await aiRematchBom({ bomId: bomOrder._id, sessionId, itemIds }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      const pendingCount = Number(response?.ai?.pendingConfirmationRows || 0);
      toast.success(
        pendingCount > 0
          ? `AI found ${pendingCount} local candidate(s). Please review them manually`
          : "AI analysis completed, but no candidates were found in the local product catalog",
      );
    } catch (error) {
      toast.error(error?.data?.message || "AI matching failed");
    }
  };

  /**
   * 接受或拒绝单条 AI 补配候选。
   * @param {string} itemId BOM 明细 ID
   * @param {"accept"|"reject"} decision 审核决定
   */
  const handleAiReview = async (itemId, decision) => {
    if (!bomOrder?._id || !itemId) return;

    try {
      const response = await reviewBomAiSuggestion({
        bomId: bomOrder._id,
        itemId,
        sessionId,
        decision,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success(decision === "accept" ? "AI match confirmed" : "AI match rejected");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to review AI match");
    }
  };

  /**
   * 打开当前云表选中行的候选确认与替换面板。
   */
  const handleOpenCandidateReview = () => {
    if (!univerApiRef.current) return;
    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems);
    if (itemIds.length !== 1) {
      toast.info("Select exactly one item row to review or change its matched part");
      return;
    }
    setCandidateReviewItemId(itemIds[0]);
    setCandidateKeyword("");
    setSearchedCandidates(null);
  };

  /**
   * 搜索当前 BOM 行的可替换候选。
   * @param {Event} event 表单提交事件
   */
  const handleCandidateSearch = async (event) => {
    event.preventDefault();
    if (!bomOrder?._id || !candidateReviewItem?._id) return;
    try {
      const response = await searchBomItemCandidates({
        bomId: bomOrder._id,
        itemId: candidateReviewItem._id,
        sessionId,
        keyword: candidateKeyword,
      }).unwrap();
      setSearchedCandidates(response?.candidates || []);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to search replacement parts");
    }
  };

  /**
   * 确认普通匹配候选或将已匹配商品替换为指定候选。
   * @param {string} productId 商品 ID
   */
  const handleConfirmCandidate = async (productId) => {
    if (!bomOrder?._id || !candidateReviewItem?._id || !productId) return;
    try {
      const response = await updateBomItem({
        bomId: bomOrder._id,
        itemId: candidateReviewItem._id,
        sessionId,
        payload: { selectedProduct: productId },
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      setCandidateReviewItemId("");
      setCandidateKeyword("");
      setSearchedCandidates(null);
      toast.success("Matched part confirmed");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to confirm matched part");
    }
  };

  /**
   * 保存在线表格编辑结果后，导出当前 BOM 的 Excel 报价文件。
   */
  const handleExportBom = async () => {
    if (!bomOrder?._id || isExporting) return;
    setIsExporting(true);
    try {
      const exportSelectedIds = new Set(
        (bomOrder.items || []).filter((item) => item.selected).map((item) => item._id),
      );
      const livePatches = extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns);
      livePatches.forEach((patch) => {
        if (patch.selected) exportSelectedIds.add(patch.itemId);
        else exportSelectedIds.delete(patch.itemId);
      });
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;

      const exportScope = exportSelectedIds.size > 0 ? "selected" : "all";
      const query = new URLSearchParams({ sessionId, scope: exportScope });
      const response = await fetch(
        `${BASE_URL || ""}${URL}/bom/${bomOrder._id}/export?${query.toString()}`,
        { credentials: "include" },
      );
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || "Failed to export BOM");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = objectUrl;
      downloadLink.download = `Creatyea_BOM_${bomOrder._id}.xlsx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success(exportScope === "selected" ? "Selected BOM items exported" : "BOM exported");
    } catch (error) {
      toast.error(error.message || "Failed to export BOM");
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * 将已选 BOM 明细批量加入现有购物车。
   */
  const addSelectedToCart = async () => {
    if (!bomOrder?._id || selectedItemIds.size === 0) return;

    try {
      if (hasRowSaveErrors) {
        toast.error("Please save failed BOM rows before adding items to cart");
        return;
      }
      const changesSaved = await flushPendingAutoSave();
      if (!changesSaved || pendingAutoSaveIdsRef.current.size > 0 || autoSaveInFlightRef.current) {
        toast.error("BOM changes are not saved yet. Please retry after saving completes");
        return;
      }
      const response = await buildBomCartItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds: Array.from(selectedItemIds),
      }).unwrap();

      (response?.cartItems || []).forEach((cartItem) => dispatch(setCartItem(cartItem)));
      toast.success(`${response?.cartItems?.length || 0} BOM item(s) added to cart`);
      navigate("/cart");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add items to cart");
    }
  };

  /**
   * 保存当前表格的待处理修改后切换到指定历史 BOM。
   * @param {string} targetBomId 目标 BOM ID
   */
  const handleOpenHistoryBom = async (targetBomId) => {
    if (String(targetBomId) === String(bomOrder?._id || "")) return;
    if (bomOrder) {
      const saved = await flushPendingAutoSave();
      if (!saved) {
        toast.error("Please resolve failed row changes before switching BOMs");
        return;
      }
    }
    setHistoryOpen(false);
    navigate(`/bom-order/${targetBomId}`);
  };

  const visibleSelectableItems = filteredItems.filter(canSelectBomItem);
  const allVisibleSelected = visibleSelectableItems.length > 0
    && visibleSelectableItems.every((item) => selectedItemIds.has(item._id));

  return (
    <main className={`bom-order-page ${bomOrder ? "is-sheet-mode" : ""}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        className="bom-hidden-file"
        onChange={handleFileChange}
        disabled={isCreatingInitialBom}
      />

      {!bomOrder ? (
        <div className="bom-upload-panel">
          <div className="bom-upload-header">
            <div className="bom-upload-icon">X</div>
            <div>
              <h1>BOM Smart Spreadsheet</h1>
              <p>Upload, paste, enter parts manually, or start with a blank spreadsheet, then review matches and add items to your cart.</p>
            </div>
          </div>
          <div className="bom-feature-grid">
            <div><strong>Fast Intelligent Matching</strong><span>Prioritizes matching by C-number and model</span></div>
            <div><strong>Inventory &amp; Pricing Details</strong><span>View stock, tiered pricing, and subtotals</span></div>
            <div><strong>Excel-like Editing</strong><span>Edit the generated spreadsheet online</span></div>
            <div><strong>Add to Cart After Confirmation</strong><span>Uses the existing shopping cart workflow</span></div>
          </div>
          <div className="bom-upload-actions">
            <button
              type="button"
              className="bom-primary-button"
              onClick={openFilePicker}
              disabled={isCreatingInitialBom}
            >
              {isRestoringBom ? "Restoring BOM..." : isUploading ? "Uploading..." : "Upload BOM Now"}
            </button>
            <button
              type="button"
              className="bom-secondary-button"
              onClick={() => {
                setClipboardInputOpen((isOpen) => !isOpen);
                setManualInputOpen(false);
              }}
              disabled={isCreatingInitialBom}
              aria-expanded={clipboardInputOpen}
              aria-controls="bom-clipboard-entry"
            >
              Paste Spreadsheet
            </button>
            <button
              type="button"
              className="bom-secondary-button"
              onClick={() => {
                setManualInputOpen((isOpen) => !isOpen);
                setClipboardInputOpen(false);
              }}
              disabled={isCreatingInitialBom}
              aria-expanded={manualInputOpen}
              aria-controls="bom-manual-entry"
            >
              Manual Entry
            </button>
            <button
              type="button"
              className="bom-secondary-button"
              onClick={handleCreateBlankBom}
              disabled={isCreatingInitialBom}
            >
              {isCreatingBlankBom ? "Creating..." : "Create Blank BOM"}
            </button>
            <a className="bom-template-link" href={getBomTemplateUrl()} download>
              Download BOM Template
            </a>
          </div>
          {clipboardInputOpen && (
            <form id="bom-clipboard-entry" className="bom-manual-entry" onSubmit={handleCreateClipboardBom}>
              <label htmlFor="bom-clipboard-input">Paste Spreadsheet with Headers</label>
              <p>Copy a two-dimensional range from Excel or WPS. Include part number/C-number and quantity headers.</p>
              <textarea
                id="bom-clipboard-input"
                rows="8"
                maxLength="200000"
                value={clipboardInput}
                onChange={(event) => setClipboardInput(event.target.value)}
                placeholder={"Manufacturer Part Number\tQuantity\tManufacturer\tPackage / Size\tDesignator\nSTM32F103C8T6\t2\tSTMicroelectronics\tLQFP-48\tU1"}
                disabled={isCreatingClipboardBom}
                autoFocus
              />
              <div className="bom-manual-entry-actions">
                <button type="button" onClick={() => setClipboardInputOpen(false)} disabled={isCreatingClipboardBom}>
                  Cancel
                </button>
                <button type="submit" className="bom-primary-button" disabled={isCreatingClipboardBom}>
                  {isCreatingClipboardBom ? "Matching..." : "Create from Clipboard"}
                </button>
              </div>
            </form>
          )}
          {manualInputOpen && (
            <form id="bom-manual-entry" className="bom-manual-entry" onSubmit={handleCreateManualBom}>
              <label htmlFor="bom-manual-input">Part Number / C-number and Quantity</label>
              <p>Enter one item per line. Separate the part number and quantity with a tab, comma, semicolon, or space.</p>
              <textarea
                id="bom-manual-input"
                rows="7"
                maxLength="50000"
                value={manualInput}
                onChange={(event) => setManualInput(event.target.value)}
                placeholder={"C25804, 10\nSTM32F103C8T6, 2\nNE555"}
                disabled={isCreatingManualBom}
                autoFocus
              />
              <div className="bom-manual-entry-actions">
                <button type="button" onClick={() => setManualInputOpen(false)} disabled={isCreatingManualBom}>
                  Cancel
                </button>
                <button type="submit" className="bom-primary-button" disabled={isCreatingManualBom}>
                  {isCreatingManualBom ? "Matching..." : "Generate BOM"}
                </button>
              </div>
            </form>
          )}
          <p className="bom-upload-help">Supports xls, xlsx, and csv; maximum file size: 10 MB</p>
          <BomHistoryPanel sessionId={sessionId} onOpen={handleOpenHistoryBom} />
        </div>
      ) : (
        <div className="bom-sheet-workspace">
          <header className="bom-sheet-topbar">
            <div className="bom-sheet-left-actions">
              <button
                type="button"
                className="bom-icon-button"
                title="Back to Upload"
                onClick={() => {
                  setBomOrder(null);
                  navigate("/bom-order");
                }}
              >
                □
              </button>
              <button type="button" className="bom-icon-button" title="Upload New BOM" onClick={openFilePicker}>
                +
              </button>
              <button
                type="button"
                className="bom-history-toggle"
                title="My BOMs"
                onClick={() => setHistoryOpen(true)}
              >
                My BOMs
              </button>
            </div>
            <div className="bom-sheet-title">
              <h1>{bomOrder.originalFileName || "BOM Online Sheet"}</h1>
              <small title={bomOrder._id}>BOM ID: {bomOrder._id}</small>
              {isAuthenticated ? (
                <div className="bom-related-orders" aria-label="Related orders">
                  <span>Orders:</span>
                  {isLoadingRelatedOrders ? (
                    <em>Loading...</em>
                  ) : relatedOrders.length > 0 ? (
                    relatedOrders.map((relatedOrder) => (
                      <Link key={relatedOrder._id} to={`/me/order/${relatedOrder._id}`}>
                        {relatedOrder.orderNo || relatedOrder._id}
                      </Link>
                    ))
                  ) : (
                    <em>No related orders</em>
                  )}
                </div>
              ) : null}
            </div>
            <div className="bom-sheet-stats">
              <div className="bom-match-card">
                <span className="bom-match-ring" style={{ "--match-rate": `${matchRate}%` }} />
                <strong>{matchRate}%</strong>
                <small>Exact Match Rate</small>
              </div>
              <label className="bom-purchase-sets">
                <span>Purchase Qty</span>
                <input
                  type="number"
                  min="1"
                  value={purchaseSets}
                  onChange={(event) => setPurchaseSets(event.target.value)}
                />
                <em>sets</em>
              </label>
            </div>
          </header>

          {historyOpen && (
            <div
              className="bom-history-overlay"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setHistoryOpen(false);
              }}
            >
              <div className="bom-history-drawer">
                <BomHistoryPanel
                  sessionId={sessionId}
                  currentBomId={bomOrder._id}
                  onOpen={handleOpenHistoryBom}
                  onClose={() => setHistoryOpen(false)}
                />
              </div>
            </div>
          )}

          <section className="bom-sheet-menu">
            <nav>
              <button type="button">Home</button>
              <button type="button">View</button>
              <button type="button" className="is-active">Filter</button>
            </nav>
            <div className="bom-sheet-actions">
              <button type="button" onClick={handleCreateItem} disabled={isCreatingItem || isDeletingItems}>
                {isCreatingItem ? "Adding..." : "Add Item"}
              </button>
              <button type="button" onClick={handleDeleteSelectedRows} disabled={isCreatingItem || isDeletingItems}>
                {isDeletingItems ? "Deleting..." : "Delete Selected Rows"}
              </button>
              <button type="button" onClick={applyPurchaseSets}>Apply Sets</button>
              <button type="button" onClick={() => saveSheetEdits()}>Save Changes</button>
              <button type="button" onClick={handleRematch} disabled={isRematching}>
                {isRematching ? "Matching..." : "Rematch"}
              </button>
              <button type="button" onClick={handleOpenCandidateReview}>
                Review / Change Part
              </button>
              <button
                type="button"
                className="bom-ai-button"
                onClick={handleAiRematch}
                disabled={isAiRematching || Number(summary.unmatchedRows || 0) === 0}
                title="Select up to 20 unmatched rows in the spreadsheet; manual confirmation is still required"
              >
                {isAiRematching ? "AI Matching..." : "AI Match Selected Rows"}
              </button>
              <button type="button" onClick={handleExportBom} disabled={isExporting}>
                {isExporting ? "Exporting..." : "Export Quote"}
              </button>
              <button
                type="button"
                onClick={openHeaderMapping}
                disabled={!bomOrder.sourceRows?.length || isReparsing}
                title={bomOrder.sourceRows?.length ? "Correct the header row and field mappings" : "Source rows were not saved for this BOM"}
              >
                {isReparsing ? "Reparsing..." : "Map Headers"}
              </button>
              <button type="button" onClick={openColumnSettings}>Customize Columns</button>
            </div>
          </section>

          {headerMappingDraft && (
            <section className="bom-column-settings bom-header-mapping" aria-label="BOM header mapping">
              <div className="bom-column-settings-heading">
                <div>
                  <strong>Map Source Headers</strong>
                  <span>Select the real header row and map source columns. Applying will rebuild all match results.</span>
                </div>
                <div className="bom-column-settings-actions">
                  <button type="button" onClick={() => setHeaderMappingDraft(null)} disabled={isReparsing}>Cancel</button>
                  <button type="button" className="is-primary" onClick={applyHeaderMapping} disabled={isReparsing}>
                    {isReparsing ? "Applying..." : "Apply & Rematch"}
                  </button>
                </div>
              </div>
              <label className="bom-header-row-field">
                <span>Header Row</span>
                <input
                  type="number"
                  min="1"
                  max={bomOrder.sourceRows.length}
                  value={headerMappingDraft.headerRowIndex + 1}
                  onChange={(event) => setHeaderMappingDraft((draft) => ({
                    ...draft,
                    headerRowIndex: Math.max(0, Number(event.target.value || 1) - 1),
                  }))}
                />
              </label>
              <div className="bom-header-mapping-grid">
                {BOM_MAPPING_FIELDS.map((field) => (
                  <label key={field.key}>
                    <span>
                      {field.label}
                      {(field.required || field.requiredGroup) && <em>*</em>}
                    </span>
                    <select
                      value={headerMappingDraft.fieldMap[field.key] ?? ""}
                      onChange={(event) => updateHeaderMappingField(field.key, event.target.value)}
                    >
                      <option value="">Not mapped</option>
                      {headerMappingColumns.map((header, columnIndex) => (
                        <option key={`${columnIndex}-${header}`} value={columnIndex}>
                          {columnIndex + 1}. {`${header || `Column ${columnIndex + 1}`}`}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <p className="bom-header-mapping-help">* Quantity is required; map at least one of Part Number or Supplier Part Number.</p>
            </section>
          )}

          {columnDraft && (
            <section className="bom-column-settings" aria-label="Customize column settings">
              <div className="bom-column-settings-heading">
                <div>
                  <strong>Customize Columns</strong>
                  <span>Select columns to display, then drag or use the arrows to reorder them. Changes are saved in this browser.</span>
                </div>
                <div className="bom-column-settings-actions">
                  <button type="button" onClick={resetColumnSettings}>Restore Defaults</button>
                  <button type="button" onClick={() => setColumnDraft(null)}>Cancel</button>
                  <button type="button" className="is-primary" onClick={applyColumnSettings}>Apply</button>
                </div>
              </div>
              <div className="bom-column-settings-list">
                {columnDraft.order.map((columnKey, columnIndex) => {
                  const column = allColumns.find((item) => item.key === columnKey);
                  if (!column) return null;
                  const visible = !columnDraft.hiddenKeys.includes(columnKey);
                  return (
                    <div
                      key={columnKey}
                      className={`bom-column-setting-item ${draggedColumnKey === columnKey ? "is-dragging" : ""}`}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", columnKey);
                        setDraggedColumnKey(columnKey);
                      }}
                      onDragEnd={() => setDraggedColumnKey("")}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => dropDraftColumn(columnKey)}
                    >
                      <span className="bom-column-drag-handle" title="Drag to reorder">⋮⋮</span>
                      <label>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleDraftColumn(columnKey)}
                        />
                        <strong>{column.title}</strong>
                      </label>
                      <small>{column.editable ? "Editable" : "Read-only"}</small>
                      <div className="bom-column-order-buttons">
                        <button
                          type="button"
                          title="Move Up"
                          disabled={columnIndex === 0}
                          onClick={() => moveDraftColumn(columnKey, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          title="Move Down"
                          disabled={columnIndex === columnDraft.order.length - 1}
                          onClick={() => moveDraftColumn(columnKey, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="bom-sheet-filterbar">
            <div className="bom-filter-tool">
              <strong>A-Z</strong>
              <span>Global Filter</span>
            </div>
            <div className="bom-filter-tool">
              <strong>⊙</strong>
              <span>Match Result Filter</span>
            </div>
            <label>
              <input
                type="checkbox"
                checked={activeStatuses.size === MATCH_STATUS.length}
                onChange={(event) => toggleAllStatus(event.target.checked)}
              />
              Select All
            </label>
            {MATCH_STATUS.map((status) => (
              <label key={status}>
                <input
                  type="checkbox"
                  checked={activeStatuses.has(status)}
                  onChange={() => toggleStatus(status)}
                />
                {STATUS_LABELS[status]}
              </label>
            ))}
          </section>

          {Object.keys(rowOperationStates).length > 0 && (
            <section className="bom-row-statusbar" aria-label="Row save and matching status">
              {(bomOrder.items || []).flatMap((item) => {
                const status = rowOperationStates[item._id];
                if (!status) return [];
                const labels = {
                  saving: "Saving...",
                  matching: "Saving & matching...",
                  saved: "Saved",
                  rematched: "Saved & rematched",
                  error: "Save failed",
                };
                return [(
                  <span key={item._id} className={`is-${status}`}>
                    Row {item.lineNo}: {labels[status]}
                  </span>
                )];
              })}
            </section>
          )}

          {aiPendingItems.length > 0 && (
            <section className="bom-ai-review-panel" aria-label="AI matches awaiting confirmation">
              <div className="bom-ai-review-heading">
                <div>
                  <strong>AI Matches Awaiting Confirmation</strong>
                  <span>AI results are suggestions only. Verify the part number, brand, package, and parameters before confirming.</span>
                </div>
                <em>{aiPendingItems.length} item(s)</em>
              </div>
              <div className="bom-ai-review-list">
                {aiPendingItems.map((item) => {
                  const suggestion = item.aiSuggestion || {};
                  const product = item.productSnapshot || {};
                  return (
                    <article key={item._id} className="bom-ai-review-item">
                      <div className="bom-ai-review-copy">
                        <span>Row {item.lineNo} · AI confidence {item.confidence || 0}%</span>
                        <strong>{product.Manufacturer_Part_Number || suggestion.suggestedMpn || "-"}</strong>
                        <small>
                          {product.Manufacturer || suggestion.suggestedManufacturer || "Unknown Brand"}
                          {suggestion.suggestedPackage ? ` · ${suggestion.suggestedPackage}` : ""}
                        </small>
                        <p>{suggestion.reason || item.matchReason}</p>
                      </div>
                      <div className="bom-ai-review-actions">
                        <button
                          type="button"
                          className="bom-ai-reject-button"
                          disabled={isReviewingAi}
                          onClick={() => handleAiReview(item._id, "reject")}
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          className="bom-ai-confirm-button"
                          disabled={isReviewingAi}
                          onClick={() => handleAiReview(item._id, "accept")}
                        >
                          Confirm Match
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {candidateReviewItem && (
            <section className="bom-candidate-panel" aria-label="Candidate matching confirmation">
              <div className="bom-candidate-heading">
                <div>
                  <strong>Candidate Review · Row {candidateReviewItem.lineNo}</strong>
                  <span>{candidateReviewItem.matchReason || "Search and select a replacement part"}</span>
                </div>
                <select
                  value={candidateReviewItem._id}
                  onChange={(event) => {
                    setCandidateReviewItemId(event.target.value);
                    setCandidateKeyword("");
                    setSearchedCandidates(null);
                  }}
                >
                  {(bomOrder.items || []).map((item) => (
                    <option key={item._id} value={item._id}>Row {item.lineNo} · {item.normalized?.originalMpn || item.normalized?.originalSupplierPartNo || "Unnamed item"}</option>
                  ))}
                </select>
              </div>
              <form className="bom-candidate-search" onSubmit={handleCandidateSearch}>
                <input
                  value={candidateKeyword}
                  onChange={(event) => setCandidateKeyword(event.target.value)}
                  placeholder="Search MPN, C-number, brand, or product name"
                />
                <button type="submit" disabled={isSearchingCandidates}>
                  {isSearchingCandidates ? "Searching..." : "Search Replacements"}
                </button>
              </form>
              {(candidateReviewItem.warnings || []).map((warning) => (
                <p key={warning} className="bom-candidate-warning">{warning}</p>
              ))}
              <div className="bom-candidate-list">
                {visibleCandidates.map((candidate) => (
                  <article key={candidate._id} className="bom-candidate-item">
                    <div className="bom-candidate-summary">
                      <span>Match score {candidate.matchScore ?? candidateReviewItem.confidence ?? 0}%</span>
                      <strong>{candidate.Manufacturer_Part_Number || candidate.CTYEA_Part_Number || candidate.name}</strong>
                      <small>{candidate.Manufacturer || "Unknown Brand"} · Stock {candidate.stock || 0}</small>
                    </div>
                    <div className="bom-candidate-diffs">
                      {(candidate.fieldDiffs || []).map((diff) => (
                        <span key={diff.field} className={`is-${diff.status}`} title={`Required: ${diff.expected || "-"}; Candidate: ${diff.actual || "-"}`}>
                          {diff.label}: {diff.status === "match" ? "Match" : diff.status === "partial" ? "Partial" : diff.status === "different" ? "Different" : "Not provided"}
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="bom-ai-confirm-button"
                      disabled={isConfirmingCandidate}
                      onClick={() => handleConfirmCandidate(candidate._id)}
                    >
                      Confirm This Part
                    </button>
                  </article>
                ))}
                {visibleCandidates.length === 0 && <p className="bom-candidate-empty">No candidates found. Try another keyword.</p>}
              </div>
            </section>
          )}

          {(isLoadingPurchasePreview || purchasePreview?.groups?.length > 0) && (
            <section className="bom-purchase-preview" aria-label="Merged purchase preview">
              <div className="bom-purchase-preview-heading">
                <div>
                  <strong>Merged Purchase Preview</strong>
                  <span>Same products are merged before MOQ rounding, tier pricing, and stock validation.</span>
                </div>
                {isLoadingPurchasePreview && <em>Refreshing...</em>}
              </div>
              {purchasePreview?.summary && (
                <div className="bom-purchase-preview-summary">
                  <span>Required <strong>{purchasePreview.summary.requestedQuantity}</strong></span>
                  <span>Purchase <strong>{purchasePreview.summary.purchaseQuantity}</strong></span>
                  <span>MOQ Difference <strong>+{purchasePreview.summary.quantityDifference}</strong></span>
                  <span>Total <strong>${formatMoney(purchasePreview.summary.totalAmount)}</strong></span>
                </div>
              )}
              <div className="bom-purchase-preview-list">
                {(purchasePreview?.groups || []).map((group) => (
                  <article key={group.productId} className={(group.insufficientStock || group.needQuote) ? "has-error" : ""}>
                    <strong>{group.productLabel || group.product?.name}</strong>
                    <span>Rows {group.lineNos.join(", ")}</span>
                    <span>{group.requestedQuantity} required → {group.purchaseQuantity} purchased</span>
                    <span>MOQ {group.minimumMultiple} · {group.priceSource}</span>
                    <span>{group.needQuote ? "Requires quote" : `$${formatMoney(group.unitPrice)} / unit · $${formatMoney(group.subtotal)}`}</span>
                    <span>Stock {group.availableStock}{group.insufficientStock ? " · Insufficient" : ""}</span>
                  </article>
                ))}
              </div>
            </section>
          )}

          <section className="bom-univer-wrap">
            <UniverBomSheet
              bomOrder={bomOrder}
              items={filteredItems}
              columns={visibleColumns}
              onReady={handleUniverReady}
              onValueChanged={handleSheetValueChanged}
            />
          </section>

          <footer className="bom-sheet-footer">
            <div className="bom-footer-left">
              <button
                type="button"
                className="bom-trash-button"
                title="Delete item rows in the current spreadsheet selection"
                onClick={handleDeleteSelectedRows}
                disabled={isDeletingItems}
              >
                ×
              </button>
              <label>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(event) => toggleVisibleSelection(event.target.checked)}
                />
                Select All
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={onlySelected}
                  onChange={(event) => setOnlySelected(event.target.checked)}
                />
                Selected Only
              </label>
              <span>{selectedItemIds.size} row(s) selected</span>
              <span>{summary.totalRows || 0} item(s) total</span>
            </div>
            <div className="bom-footer-summary">
              <span>{summary.totalRows || 0} row(s) total</span>
              <span>{summary.matchedRows || 0} exact match(es)</span>
              <span>{summary.pendingRows || 0} pending</span>
              <span>{summary.unmatchedRows || 0} unmatched</span>
            </div>
            <div className="bom-footer-actions">
              <div>
                <span>Total:</span>
                <strong>${formatMoney(purchaseTotalAmount)}</strong>
              </div>
              <button
                type="button"
                className="bom-primary-button"
                onClick={addSelectedToCart}
                disabled={isAdding || selectedItemIds.size === 0 || hasPurchaseBlockingIssue || hasUnsafePurchaseEdits}
                title={hasUnsafePurchaseEdits ? "Wait for all BOM row changes to save successfully before adding to cart" : ""}
              >
                {isAdding
                  ? "Adding..."
                  : (hasRowSaveErrors ? "Fix save errors" : (hasPendingRowChanges ? "Saving changes..." : "Add to Cart"))}
              </button>
            </div>
          </footer>
        </div>
      )}
    </main>
  );
};

export default BomOrderPage;
